import { isUndefined, isEmpty } from 'lodash'
import moment from 'moment'
import {
  adapters,
  getOptionsCacheKey,
  getOptionsRemoteUrl
} from 'utils/documents'
import { linkedDocsCache } from './RemoteOptions'

const autogenerated = () => Date.now()

const scenarioDep = ({ getValue, scenario }) =>
  getValue && scenario ? getValue({ scenario }) : null

const remoteOptionsDep = async ({ field, getValue, instanceId }) => {
  const { documentType } = adapters.getCellParams(field)

  const options = await linkedDocsCache.getOptions(
    getOptionsCacheKey(instanceId, documentType),
    getOptionsRemoteUrl(instanceId, documentType)
  )

  return getValue ? getValue(options) : null
}

const sysdate = () =>
  moment(new Date()).hours(0).minutes(0).seconds(0).utcOffset(0, true).format()

const detailDep = async ({ field, instanceId }) => {
  const { parentTitle, detailTitle } = field.value
  const { documentType, optionsKey } = adapters.getCellParams(field)

  const { list } = await linkedDocsCache.getOptions(
    getOptionsCacheKey(instanceId, documentType),
    getOptionsRemoteUrl(instanceId, documentType)
  )
  if (isEmpty(list)) return null

  const parentObject = list.find((option) => option.title === parentTitle)
  const { uri } = parentObject[optionsKey].find(
    (detail) => detail.title === detailTitle
  )

  return uri
}

const valueResolversMap = {
  autogenerated,
  scenarioDep,
  remoteOptionsDep,
  sysdate,
  detailDep
}

const makeGetDefaultValues =
  (
    valueResolvers // Inject value resolvers so they can be mocked/spyed on in tests
  ) =>
  async (fields, { defaultValueGetter, ...rest }) => {
    const fieldsWithDefault = fields.filter(
      (field) => adapters.getId(field) && !isUndefined(field.value)
    )

    const defaultValues = {}

    for (const field of fieldsWithDefault) {
      const fieldId = adapters.getId(field)

      if (!field.value.type) {
        defaultValues[fieldId] = field.value // Literal static value
        continue
      }

      const params = { ...rest, field, getValue: defaultValueGetter?.[fieldId] }

      // Support chained value getters. This has not been tested thoroughly...
      const getters = [].concat(field.value.type)

      for (const getter of getters) {
        const resolveValue = valueResolvers[getter]
        defaultValues[fieldId] = await resolveValue(params)
        if (defaultValues[fieldId]) break // Do not call the next getters if we have a value, they act as fallbacks
      }
    }

    return defaultValues
  }

export const getDefaultValues = makeGetDefaultValues(valueResolversMap)

// Exported only for testing reasons
export const testing = {
  makeGetDefaultValues,
  valueResolversMap
}
